import type { GoogleCampaignSettingType } from "@daytrip/legacy-enums";
import { CountryBankType, CountryGroup, Currency, PaymentProvider } from "@daytrip/legacy-enums";
import { Expose, Type } from "class-transformer";
import {
    IsArray,
    IsBoolean,
    IsDefined,
    IsEnum,
    IsInt,
    IsNumber,
    IsString,
    IsUUID,
    MaxLength,
    ValidateNested,
} from "class-validator";
import { v4 as uuid } from "uuid";

import { GField, GInputType, GObjectType } from "../graphql";
import { CDBaseEntity, CDColumn, CDEntity, CDObjectIdColumn } from "../orm";

import { CountryInvoiceInfo } from "./CountryInvoiceInfo";
import { VehicleTypeCosts } from "./VehicleTypeCosts";

@CDEntity("countries")
@GObjectType("Country")
@GInputType("CountryInput")
export class Country extends CDBaseEntity {
    @GField()
    @CDObjectIdColumn()
    @IsUUID("4")
    @Expose()
    public _id: string = uuid();

    @CDColumn()
    @MaxLength(16)
    @IsString()
    @IsDefined()
    @Expose()
    @GField()
    public isoCode: string = "";

    @CDColumn()
    @MaxLength(16)
    @IsString()
    @IsDefined()
    @Expose()
    @GField()
    public continent: string = "";

    @CDColumn()
    @MaxLength(64)
    @IsString()
    @IsDefined()
    @Expose()
    @GField()
    public localName: string = "";

    @GField()
    @CDColumn()
    @MaxLength(64)
    @IsString()
    @IsDefined()
    @Expose()
    public englishName: string = "";

    @GField()
    @CDColumn()
    @MaxLength(64)
    @IsString()
    @IsDefined()
    @Expose()
    public timeZoneName: string = ""; // DEPRECATED

    @CDColumn()
    @IsInt()
    public countryGroup: CountryGroup;

    @GField()
    @CDColumn()
    @IsNumber()
    @IsDefined()
    @Expose()
    public guidePrice: number = 10;

    @GField()
    @CDColumn()
    @IsNumber()
    @IsDefined()
    @Expose()
    public guideMinimalPrice: number = 50;

    @GField()
    @CDColumn()
    @IsNumber()
    @IsDefined()
    @Expose()
    public roundtripGuideCoefficient: number = 1.5;

    @GField()
    @CDColumn()
    @IsNumber()
    @IsDefined()
    @Expose()
    public roundtripMinimumCoefficient: number = 1.5;

    @GField()
    @CDColumn()
    @IsNumber()
    @IsDefined()
    @Expose()
    public guideLocationPrice: number = 10;

    @GField()
    @CDColumn()
    @IsNumber()
    @IsDefined()
    @Expose()
    public taxFeeCoefficient: number = 0;

    @CDColumn()
    @IsArray()
    @IsDefined()
    @Expose()
    @Type(() => VehicleTypeCosts)
    @ValidateNested({ each: true })
    public vehicleTypeCosts: VehicleTypeCosts[] = [];

    @CDColumn()
    @IsNumber()
    @Expose()
    public googleTargetingId: number | undefined;

    @CDColumn()
    @IsEnum(Currency)
    @Expose()
    public currency?: Currency;

    @CDColumn()
    @IsEnum(Currency)
    @Expose()
    public pricingCurrency = Currency.Euro;

    @CDColumn()
    @IsNumber()
    @Expose()
    public googleCampaignSettingType: GoogleCampaignSettingType | undefined;

    @CDColumn()
    @IsBoolean()
    @Expose()
    public isInSchengen: boolean | undefined;

    @CDColumn()
    @IsBoolean()
    @Expose()
    public isPaymentCountry: boolean | undefined;

    @CDColumn()
    @IsEnum(CountryBankType)
    @Expose()
    public bankType = CountryBankType.IBAN;

    @GField()
    @CDColumn()
    @IsString()
    @Expose()
    public callingCode: string = "";

    @GField()
    @CDColumn()
    @IsString()
    @Expose()
    public emergencyPhone: string;

    @CDColumn()
    @IsBoolean()
    @Expose()
    public applyTollPriceCoefficient: boolean = true;

    @CDColumn()
    @Expose()
    @Type(() => CountryInvoiceInfo)
    public invoiceInfo?: CountryInvoiceInfo | null = null;

    @CDColumn()
    @Expose()
    @IsEnum(PaymentProvider)
    public paymentProvider: PaymentProvider = PaymentProvider.Mangopay;
}
